import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { z } from "zod";

const zodiacSigns = [
  {
    name: "Aries",
    emoji: "♈",
    startMonth: 3,
    startDay: 21,
    endMonth: 4,
    endDay: 19,
    description: "Valiente, directo y apasionado",
    path: "/horoscopo-aries"
  },
  {
    name: "Tauro",
    emoji: "♉",
    startMonth: 4,
    startDay: 20,
    endMonth: 5,
    endDay: 20,
    description: "Paciente, confiable y devoto",
    path: "/horoscopo-tauro"
  },
  {
    name: "Géminis",
    emoji: "♊",
    startMonth: 5,
    startDay: 21,
    endMonth: 6,
    endDay: 20,
    description: "Versátil, expresivo y curioso",
    path: "/horoscopo-geminis"
  },
  {
    name: "Cáncer",
    emoji: "♋",
    startMonth: 6,
    startDay: 21,
    endMonth: 7,
    endDay: 22,
    description: "Emocional, intuitivo y protector",
    path: "/horoscopo-cancer"
  },
  {
    name: "Leo",
    emoji: "♌",
    startMonth: 7,
    startDay: 23,
    endMonth: 8,
    endDay: 22,
    description: "Dramático, carismático y líder nato",
    path: "/horoscopo-leo"
  },
  {
    name: "Virgo",
    emoji: "♍",
    startMonth: 8,
    startDay: 23,
    endMonth: 9,
    endDay: 22,
    description: "Analítico, práctico y trabajador",
    path: "/horoscopo-virgo"
  },
  {
    name: "Libra",
    emoji: "♎",
    startMonth: 9,
    startDay: 23,
    endMonth: 10,
    endDay: 22,
    description: "Diplomático, justo y social",
    path: "/horoscopo-libra"
  },
  {
    name: "Escorpio",
    emoji: "♏",
    startMonth: 10,
    startDay: 23,
    endMonth: 11,
    endDay: 21,
    description: "Intenso, apasionado y transformador",
    path: "/horoscopo-escorpio"
  },
  {
    name: "Sagitario",
    emoji: "♐",
    startMonth: 11,
    startDay: 22,
    endMonth: 12,
    endDay: 21,
    description: "Optimista, aventurero y filosófico",
    path: "/horoscopo-sagitario"
  },
  {
    name: "Capricornio",
    emoji: "♑",
    startMonth: 12,
    startDay: 22,
    endMonth: 1,
    endDay: 19,
    description: "Ambicioso, disciplinado y responsable",
    path: "/horoscopo-capricornio"
  },
  {
    name: "Acuario",
    emoji: "♒",
    startMonth: 1,
    startDay: 20,
    endMonth: 2,
    endDay: 18,
    description: "Innovador, independiente y humanitario",
    path: "/horoscopo-acuario"
  },
  {
    name: "Piscis",
    emoji: "♓",
    startMonth: 2,
    startDay: 19,
    endMonth: 3,
    endDay: 20,
    description: "Compasivo, artístico y soñador",
    path: "/horoscopo-piscis"
  }
];

// Days in each month (index 0 = January)
const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Validation schema for date inputs
const dateSchema = z.object({
  day: z.number().int().min(1, { message: "El día debe ser al menos 1" }).max(31, { message: "El día no puede ser mayor a 31" }),
  month: z.number().int().min(1, { message: "Selecciona un mes válido" }).max(12, { message: "El mes debe estar entre 1 y 12" })
}).refine(data => {
  // Validate that the day is valid for the selected month
  return data.day <= daysInMonth[data.month - 1];
}, {
  message: "Día inválido para el mes seleccionado",
  path: ["day"]
});

const WesternZodiacCalculator = () => {
  const [day, setDay] = useState<string>("");
  const [month, setMonth] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [result, setResult] = useState<typeof zodiacSigns[0] | null>(null);

  const months = [
    { value: "1", label: "Enero" },
    { value: "2", label: "Febrero" },
    { value: "3", label: "Marzo" },
    { value: "4", label: "Abril" },
    { value: "5", label: "Mayo" },
    { value: "6", label: "Junio" },
    { value: "7", label: "Julio" },
    { value: "8", label: "Agosto" },
    { value: "9", label: "Septiembre" },
    { value: "10", label: "Octubre" },
    { value: "11", label: "Noviembre" },
    { value: "12", label: "Diciembre" },
  ];

  const calculateZodiacSign = (dayNum: number, monthNum: number) => {
    const sign = zodiacSigns.find(sign => {
      if (sign.startMonth === sign.endMonth) {
        return monthNum === sign.startMonth && dayNum >= sign.startDay && dayNum <= sign.endDay;
      } else if (sign.startMonth > sign.endMonth) {
        return (
          (monthNum === sign.startMonth && dayNum >= sign.startDay) ||
          (monthNum === sign.endMonth && dayNum <= sign.endDay)
        );
      } else {
        return (
          (monthNum === sign.startMonth && dayNum >= sign.startDay) ||
          (monthNum === sign.endMonth && dayNum <= sign.endDay) ||
          (monthNum > sign.startMonth && monthNum < sign.endMonth)
        );
      }
    });

    if (sign) {
      setResult(sign);
    }
  };

  const validateAndCalculate = (dayValue: string, monthValue: string) => {
    // Clear previous error and result
    setError("");
    setResult(null);

    // Don't validate if either field is empty
    if (!dayValue || !monthValue || dayValue.trim() === "" || monthValue.trim() === "") {
      return;
    }

    // Parse values
    const dayNum = parseInt(dayValue, 10);
    const monthNum = parseInt(monthValue, 10);

    // Check for invalid numbers
    if (isNaN(dayNum) || isNaN(monthNum)) {
      setError("Por favor ingresa valores numéricos válidos");
      return;
    }

    // Validate with zod schema
    const validation = dateSchema.safeParse({ day: dayNum, month: monthNum });
    
    if (!validation.success) {
      setError(validation.error.errors[0].message);
      return;
    }

    // If validation passes, calculate the zodiac
    calculateZodiacSign(dayNum, monthNum);
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDay(value);
    validateAndCalculate(value, month);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setMonth(value);
    validateAndCalculate(day, value);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="lg" className="text-lg">
          ¿Cuál es mi Signo del Zodiaco?
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-xl text-center">
            Descubre tu Signo Zodiacal
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Día de nacimiento:
            </label>
            <Input
              type="number"
              placeholder="Día (1-31)"
              value={day}
              onChange={handleDayChange}
              min={1}
              max={31}
              className={cn("text-center", error && "border-red-500")}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Mes de nacimiento:
            </label>
            <select
              value={month}
              onChange={handleMonthChange}
              className={cn(
                "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                error && "border-red-500"
              )}
            >
              <option value="">Selecciona el mes</option>
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}

          {result && (
            <Card className="gradient-celestial">
              <CardContent className="p-4 text-center space-y-3">
                <div className="text-5xl">{result.emoji}</div>
                <div>
                  <h3 className="text-xl font-bold">
                    {result.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {result.description}
                  </p>
                </div>
                <Link to={result.path}>
                  <Button variant="outline" size="sm" className="w-full">
                    Ver Horóscopo Completo
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WesternZodiacCalculator;
